/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';

/**
 * External dependencies
 */
import { useState, useMemo } from 'react';

/**
 * Internal dependencies
 */
import { useJobs } from '../hooks/useJobs';
import type { JobBoardProvider } from '../types';
import SearchIcon from '../../../../assets/svg/icon/search.svg';
import JobCard from './JobCard';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import FilterDropdown, { FilterOptions } from './FilterDropdown';

interface ViewProps {
	provider: JobBoardProvider;
	settings?: Record< string, unknown >;
}

export default function View( { provider, settings = {} }: ViewProps ) {
	const { jobs, error, loading } = useJobs( provider, settings );
	const [
		searchTerm,
		setSearchTerm,
	] = useState( '' );
	const [
		filters,
		setFilters,
	] = useState< FilterOptions >( {
		employmentType: '',
		location: '',
		datePosted: '',
	} );
	const [
		sortBy,
		setSortBy,
	] = useState< string >( '' );

	// Extract unique values for filter options
	const availableOptions = useMemo( () => {
		const employmentTypes = Array.from( new Set( jobs.map( ( job ) => job.employmentType ).filter( ( e ): e is string => Boolean( e ) ) ) ).sort();
		const locations = Array.from( new Set( jobs.map( ( job ) => job.location ).filter( ( l ): l is string => Boolean( l ) ) ) ).sort();

		return { employmentTypes, locations };
	}, [ jobs ] );

	// Filter jobs
	const filteredJobs = useMemo( () => {
		return jobs.filter( ( job ) => {
			// Search filter
			const matchesSearch =
				searchTerm === '' ||
				job.title.toLowerCase().includes( searchTerm.toLowerCase() ) ||
				job.description.toLowerCase().includes( searchTerm.toLowerCase() ) ||
				job.location.toLowerCase().includes( searchTerm.toLowerCase() );

			// Employment type filter
			const matchesEmploymentType = filters.employmentType === '' || job.employmentType === filters.employmentType;

			// Location filter
			const matchesLocation = filters.location === '' || job.location === filters.location;

			// Date posted filter
			const matchesDatePosted = filters.datePosted === '' || isWithinDays( job.postedDate, parseInt( filters.datePosted ) );

			return matchesSearch && matchesEmploymentType && matchesLocation && matchesDatePosted;
		} );
	}, [
		jobs,
		searchTerm,
		filters,
	] );

	// Sort jobs
	const sortedJobs = useMemo( () => {
		const jobsCopy = [ ...filteredJobs ];

		if ( sortBy === 'date' ) {
			return jobsCopy.sort( ( a, b ) => {
				const dateA = new Date( a.postedDate ).getTime();
				const dateB = new Date( b.postedDate ).getTime();
				return dateB - dateA; // Newest first
			} );
		}

		if ( sortBy === 'title' ) {
			return jobsCopy.sort( ( a, b ) => a.title.localeCompare( b.title ) );
		}

		if ( sortBy === 'location' ) {
			return jobsCopy.sort( ( a, b ) => a.location.localeCompare( b.location ) );
		}

		return jobsCopy;
	}, [
		filteredJobs,
		sortBy,
	] );

	function isWithinDays( dateString: string, days: number ): boolean {
		if ( ! dateString ) {
			return false;
		}

		const jobDate = new Date( dateString );
		const now = new Date();
		const diffTime = now.getTime() - jobDate.getTime();
		const diffDays = diffTime / ( 1000 * 60 * 60 * 24 );

		return diffDays <= days;
	}

	function handleFilterChange( key: keyof FilterOptions, value: string ) {
		setFilters( ( prev ) => ( { ...prev, [ key ]: value } ) );
	}

	function handleClearFilters() {
		setFilters( {
			employmentType: '',
			location: '',
			datePosted: '',
		} );
	}

	return (
		<div className="job-board">
			<div className="job-board__filters">
				<div className="job-board__search">
					<SearchIcon className="job-board__search-icon" aria-hidden="true" />
					<input
						type="text"
						className="job-board__search-input"
						placeholder={ __( 'Search jobs', 'ardent' ) }
						value={ searchTerm }
						onChange={ ( e ) => setSearchTerm( e.target.value ) }
						aria-label={ __( 'Search jobs', 'ardent' ) }
					/>
				</div>

				<FilterDropdown
					filters={ filters }
					onFilterChange={ handleFilterChange }
					onClearAll={ handleClearFilters }
					availableOptions={ availableOptions }
				/>

				<div className="job-board__sort">
					<select className="job-board__sort-select" value={ sortBy } onChange={ ( e ) => setSortBy( e.target.value ) } aria-label={ __( 'Sort by', 'ardent' ) }>
						<option value="">{ __( 'Sort by', 'ardent' ) }</option>
						<option value="date">{ __( 'Date Posted', 'ardent' ) }</option>
						<option value="title">{ __( 'Job Title', 'ardent' ) }</option>
						<option value="location">{ __( 'Location', 'ardent' ) }</option>
					</select>
				</div>
			</div>

			{ error && <ErrorState error={ error } /> }
			{ ! error && loading && <LoadingState /> }
			{ ! error && ! loading && jobs.length === 0 && <EmptyState provider={ provider } /> }
			{ ! error && jobs.length > 0 && ! loading && sortedJobs.length === 0 && (
				<div className="job-board__no-results">
					<p>{ __( 'No positions match your search criteria.', 'ardent' ) }</p>
				</div>
			) }
			{ ! error && jobs.length > 0 && ! loading && sortedJobs.length > 0 && (
				<div className="job-board__list">
					{ sortedJobs.map( ( job, index ) => (
						<JobCard key={ job.id || `${ job.provider }-${ index }` } job={ job } />
					) ) }
				</div>
			) }
		</div>
	);
}
